// Copyright (c) StrangeBee 2023

import React, { ReactElement, useEffect, useMemo } from 'react';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import client from '@Client';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { useLocalMessage } from '@Helpers/locale';
import { THLayout } from '@Molecules/THLayout';
import { computeEventList } from '@Organisms/Case/eventList';
import useListQuery from '@Hooks/useListQuery';
import { MarkdownVisualizer } from '@Molecules/MarkdownVisualizer/MarkdownVisualizer';
import useCasePermissions from '@Hooks/useCasePermissions';
import { OrgPermissionsEnum, PageOutput } from 'thehive-sdk';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import { ICONS } from '@Constants/iconsApp';
import { PageTemplateManage } from '@Organisms/PageTemplate/PageTemplateManage/PageTemplateManage';
import { useDrawer } from '@Hooks/useDrawer';

import './CasePages.scss';

const CasePages = (): ReactElement => {
    const i = useLocalMessage();
    const { caseId, pageId } = useParams<{ caseId: string; pageId?: string }>();
    const history = useHistory();

    const { visible, setVisible } = useDrawer({});

    const {
        data: pageList = [],
        refetch: listRefetch,
        status,
    } = useListQuery<PageOutput[]>({
        controller: client.CaseController,
        queryParams: {
            selector: {
                head: [{ name: 'getCase', idOrName: caseId }, { name: 'pages' }],
                name: 'get-all-pages-from-case',
            },
            sort: ['+_createdAt'],
        },
    });

    const casePageListCategories = useMemo(
        () =>
            pageList.reduce((acc, { category }) => (acc.includes(category) ? acc : [...acc, category]), [] as string[]),
        [pageList],
    );

    const hasPermissions = useCasePermissions({ caseId, permissionsNeeded: [OrgPermissionsEnum.MANAGE_PAGE] });

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.CaseController,
                locale: i,
                onSuccess: (_, message) => {
                    if (message) {
                        openMessage('success', message);
                    }
                    listRefetch();
                },
                onError: handleNotificationError,
            }),
        [listRefetch, i],
    );

    useEffect(() => {
        // ugly -> to avoid pageId in URL when there is no page
        if (!pageList.length && status === 'success' && pageId) {
            history.push(RouteBuilder.compute('cases.pages', { caseId }));
        }
    }, [caseId, history, pageId, pageList.length, status]);

    return (
        <>
            <THLayout>
                <THLayout.Content>
                    {pageList.length ? (
                        <MarkdownVisualizer
                            className="case-pages"
                            onEdit={(page, pageId) => eventList.pages.onEdit(page, caseId, pageId)}
                            CreateButton={({ className }: { className?: string }): ReactElement => (
                                <AddButton
                                    onClick={() => setVisible(true)}
                                    className={className}
                                    block
                                    label={i('page.create.drawer.label')}
                                />
                            )}
                            onDelete={(pageId) => eventList.pages.onDelete(caseId, pageId)}
                            list={pageList}
                            hasPermissions={hasPermissions}
                        />
                    ) : (
                        <DisplayEmptyData spin={status === 'loading'} label={i('page.none')}>
                            {hasPermissions && (
                                <THButton onClick={() => setVisible(true)} type="link">
                                    {i('page.create.drawer.label')}
                                </THButton>
                            )}
                        </DisplayEmptyData>
                    )}
                </THLayout.Content>
            </THLayout>

            {visible && (
                <PageTemplateManage
                    drawerTitle={i('page.create.drawer.label')}
                    ctaMode={['create', 'use']}
                    triggerElem={<></>}
                    onCreate={(page) => eventList.pages.onAdd(page, caseId)}
                    onFinish={(page) => eventList.pages.onAdd(page, caseId)}
                    noContent
                    visible={visible}
                    setVisible={setVisible}
                    defaulPageTemplateCategories={casePageListCategories}
                />
            )}
        </>
    );
};

export const Route: RouteType = {
    menu: {
        label: 'case.layout.nav-bar.pages',
        icon: <ICONS.FileOutlined />,
    },
    exact: true,
    path: RouteBuilder.compute('cases.pages'),
    content: <CasePages />,
    space: ['user'],
    breadcrumb: { label: 'breadcrumb.case.pages' },
};

const BreadcrumbLabel = (): ReactElement => {
    const match = useRouteMatch(RouteBuilder.compute('cases.page'));
    const { pageId = '' } = (match?.params as any) || {};

    return <>{pageId}</>;
};

export const RouteDetails: RouteType = {
    path: RouteBuilder.compute('cases.page'),
    content: <CasePages />,
    space: ['user'],
    breadcrumb: { label: <BreadcrumbLabel />, isLink: false },
};
